import React from 'react';
import { useTranslations } from 'next-intl';
import { NextUI } from '@/components/NextUI';
import { APP_EVENTS, logHubxEvent } from '@/app/providers/analytics';
import { formatFileSize } from '@/helpers/common';
import ProtectPdfField from '../protect/ProtectPdfField';
import ProtectPdfButton from '../protect/ProtectPdfButton';
import CloseCrossSmall from '../protect/icons/CloseCrossSmall';
import ProtectedFile from '../protect/icons/ProtectedFile';
import UnlockStatusGlow from './icons/UnlockStatusGlow';
import PasswordRemovalIllustration from './icons/PasswordRemovalIllustration';
import type { ProtectPdfUnlockResult } from '../protect/ProtectPdfCurrentPasswordModal';
import { UNLOCK_PDF_PROCESSING_DURATION_MS } from '../../../utils/unlockPdf';

export type UnlockPdfProcessingModalData = {
  requestId?: number;
  fileName: string;
  fileSize: number;
  pageCount?: number;
  durationMs?: number;
  analyticsSource?: string;
  onUnlock: (password: string) => Promise<ProtectPdfUnlockResult>;
  onTimeout: () => void;
  onDismiss: () => void;
};

type ProcessingMode = 'progress' | 'password';

type ProcessingPasswordError = 'empty' | 'incorrect' | 'failed';

type UnlockPdfProcessingModalProps = {
  data?: UnlockPdfProcessingModalData;
  hideModal: () => void;
};

const PROGRESS_TICK_MS = 200;

const UnlockPdfProcessingForm = ({
  data,
  hideModal,
}: UnlockPdfProcessingModalProps) => {
  const t = useTranslations('unlock_pdf_flow');
  const tProtect = useTranslations('protect_pdf_flow');
  const [mode, setMode] = React.useState<ProcessingMode>('progress');
  const [progress, setProgress] = React.useState(0);
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState<ProcessingPasswordError | null>(
    null
  );
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const isMountedRef = React.useRef(true);
  const isSubmittingRef = React.useRef(false);
  const hasExpiredRef = React.useRef(false);
  const latestDataRef = React.useRef(data);
  const analyticsSource = data?.analyticsSource;
  const durationMs = data?.durationMs ?? UNLOCK_PDF_PROCESSING_DURATION_MS;
  const estimatedSeconds = Math.round(durationMs / 1000);

  latestDataRef.current = data;

  React.useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  React.useEffect(() => {
    logHubxEvent({
      eventName: APP_EVENTS.LND_PROCESSING_FILE,
      source: analyticsSource,
    });
  }, [analyticsSource]);

  React.useEffect(() => {
    if (mode !== 'password') {
      return;
    }

    logHubxEvent({
      eventName: APP_EVENTS.LND_PASSWORD_INPUT,
      source: analyticsSource,
    });
  }, [analyticsSource, mode]);

  React.useEffect(() => {
    const startedAt = Date.now();
    const interval = window.setInterval(() => {
      setProgress(Math.min(1, (Date.now() - startedAt) / durationMs));
    }, PROGRESS_TICK_MS);
    const timeout = window.setTimeout(() => {
      window.clearInterval(interval);
      setProgress(1);
      hasExpiredRef.current = true;

      if (isSubmittingRef.current) {
        return;
      }

      latestDataRef.current?.onTimeout();
    }, durationMs);

    return () => {
      window.clearInterval(interval);
      window.clearTimeout(timeout);
    };
  }, [durationMs]);

  const errorMessage =
    error === 'empty'
      ? tProtect('empty_password_error')
      : error === 'incorrect'
        ? tProtect('incorrect_password_error')
        : error === 'failed'
          ? tProtect('unlock_failed')
          : null;
  const canSubmit = password.length > 0 && error !== 'incorrect';
  const fileMeta = data
    ? [
        typeof data.pageCount === 'number'
          ? t('page_count', { count: data.pageCount })
          : null,
        formatFileSize(data.fileSize),
      ]
        .filter(Boolean)
        .join(' • ')
    : '';

  const handleChange = (value: string) => {
    setPassword(value);
    setError(null);
  };

  const handleDismiss = React.useCallback(() => {
    if (isSubmittingRef.current) {
      return;
    }

    data?.onDismiss();
    hideModal();
  }, [data, hideModal]);

  const handleWaitClick = () => {
    logHubxEvent({
      eventName: APP_EVENTS.BTN_WAIT_I_KNOW_PASSWORD,
      source: analyticsSource,
    });
    setMode('password');
  };

  const handleBack = () => {
    if (isSubmittingRef.current) {
      return;
    }

    setMode('progress');
    setPassword('');
    setError(null);
  };

  const submit = React.useCallback(async () => {
    if (isSubmittingRef.current || !data) {
      return;
    }

    if (password.length === 0) {
      setError('empty');
      return;
    }

    if (error === 'incorrect') {
      return;
    }

    logHubxEvent({
      eventName: APP_EVENTS.BTN_PASSWORD_CONTINUE,
      source: analyticsSource,
    });

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    const result = await data.onUnlock(password);

    isSubmittingRef.current = false;

    if (result === 'unlocked' || !isMountedRef.current) {
      return;
    }

    if (hasExpiredRef.current) {
      data.onTimeout();
      return;
    }

    setIsSubmitting(false);
    setError(result === 'wrong_password' ? 'incorrect' : 'failed');
  }, [analyticsSource, data, error, password]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void submit();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLFormElement>) => {
    if (
      event.key !== 'Enter' ||
      event.nativeEvent.isComposing ||
      !(event.target instanceof HTMLInputElement)
    ) {
      return;
    }

    event.preventDefault();
    void submit();
  };

  return (
    <NextUI.Modal
      className="mx-4 max-w-[670px] rounded-[16px] shadow-none"
      classNames={{
        content:
          'relative max-h-[calc(100dvh-32px)] overflow-x-hidden overflow-y-auto rounded-[16px] bg-white px-6 py-8 shadow-none md:px-12',
        backdrop: 'bg-[rgba(124,124,124,0.5)]',
      }}
      defaultOpen={true}
      name="UnlockPdfProcessing"
      backdrop="opaque"
      hideCloseButton={true}
      isDismissable={false}
      isKeyboardDismissDisabled={isSubmitting}
      onClose={handleDismiss}
    >
      <UnlockStatusGlow className="pointer-events-none absolute left-0 top-0 h-[152px] w-full" />

      <div className="relative flex flex-col gap-8">
        <div className="flex flex-col items-center gap-4 py-4">
          <PasswordRemovalIllustration className="h-32 w-[200px] shrink-0" />
          <span className="w-full break-words text-center font-barlow text-[28px] font-semibold leading-[34px] text-[#454655]">
            {t('processing_title')}
          </span>
        </div>

        <div className="flex w-full items-center gap-2 rounded-[12px] border border-solid border-[#E5E6EB] px-4 py-3">
          <span className="relative h-9 w-9 shrink-0">
            <ProtectedFile className="absolute left-[7px] top-0 h-9 w-7" />
          </span>
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="truncate font-barlow text-[17px] font-normal leading-[normal] text-[#454655]">
              {data?.fileName}
            </span>
            <span className="truncate font-barlow text-[15px] font-normal leading-[normal] text-[#787887]">
              {fileMeta}
            </span>
          </div>
        </div>

        <div className="flex w-full flex-col gap-2">
          <div
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress * 100)}
            className="relative h-2 w-full overflow-hidden rounded-[100px] bg-[#EDF2FF]"
          >
            <div
              className="absolute inset-0 animate-unlockProgressFill rounded-[100px] bg-[#4B7EFF] will-change-transform"
              style={{ animationDuration: `${durationMs}ms` }}
            />
          </div>
          <div className="flex w-full items-start justify-between gap-2 font-barlow text-[15px] leading-[normal]">
            <span className="font-semibold text-[#454655]">
              {Math.round(progress * 100)}%
            </span>
            <span className="font-normal text-[#787887]">
              {t('estimated_time', { seconds: estimatedSeconds })}
            </span>
          </div>
        </div>

        {mode === 'progress' ? (
          <button
            type="button"
            onClick={handleWaitClick}
            className="w-full text-center font-barlow text-[17px] font-semibold leading-[normal] text-[#4B7EFF] outline-none"
          >
            {t('wait_password_button')}
          </button>
        ) : (
          <form
            noValidate
            onSubmit={handleSubmit}
            onKeyDown={handleKeyDown}
            className="flex flex-col gap-8"
          >
            <ProtectPdfField
              id="unlock-pdf-current-password"
              label={t('current_password_label')}
              placeholder={t('password_placeholder')}
              value={password}
              onChange={handleChange}
              autoFocus
              errorMessage={errorMessage}
              disabled={isSubmitting}
            />

            <div className="flex w-full gap-2">
              <div className="min-w-0 flex-1">
                <ProtectPdfButton
                  variant="secondary"
                  onClick={handleBack}
                  isDisabled={isSubmitting}
                >
                  {t('back_button')}
                </ProtectPdfButton>
              </div>
              <div className="min-w-0 flex-1">
                <ProtectPdfButton
                  type="submit"
                  isDisabled={!canSubmit}
                  isLoading={isSubmitting}
                >
                  {t('unlock_button')}
                </ProtectPdfButton>
              </div>
            </div>
          </form>
        )}
      </div>

      <button
        type="button"
        aria-label={tProtect('close_aria')}
        onClick={handleDismiss}
        className="absolute left-5 top-5 z-10 flex h-8 w-8 items-center justify-center text-[#454655] outline-none"
      >
        <CloseCrossSmall className="h-8 w-8" />
      </button>
    </NextUI.Modal>
  );
};

const UnlockPdfProcessingModal = (props: UnlockPdfProcessingModalProps) => (
  <UnlockPdfProcessingForm
    key={props.data?.requestId ?? 'unlock-pdf-processing'}
    {...props}
  />
);

export default UnlockPdfProcessingModal;
